/**
 * Storage for the Project > Folder containers that organise workspaces.
 *
 * Firestore collection `folders` is the source of truth when cloud sync is on;
 * IndexedDB store `folders` mirrors it for offline reads and is the only store
 * when cloud sync is off. Unlike workspaces, folders are never silently created
 * "local-only" while cloud sync is on: a failed cloud write throws so the UI can
 * tell the user, and a deleted folder never resurrects from a stale mirror.
 */

import { getFirestore, doc, setDoc, getDocs, collection, query, where, writeBatch } from 'firebase/firestore';
import { getFirebaseApp, isFirebaseActive, openLocalDB, FOLDERS_STORE, requireAuth, validateParams, withCloudTimeout, isOffline, describeCloudError } from './storage';
import { normalizeFolderName } from './folderTree';
import { logger } from './logger';
import type { FolderColor } from './folderColors';

export { FOLDER_COLORS, type FolderColor } from './folderColors';

export type FolderKind = 'project' | 'folder';

export interface WorkspaceFolder {
    id: string;
    ownerId: string;
    name: string;
    kind: FolderKind;
    /** null for projects (roots); a project or folder id for folders. */
    parentId: string | null;
    color?: FolderColor;
    createdAt: number;
    updatedAt: number;
    /**
     * Who a project is shared with (netlify/functions/api/project-sharing.ts); its folders carry
     * the same list. The owner sees how many; the people it is shared with read it through this.
     */
    sharedUserIds?: string[];
    /** Someone else's project, or a folder in it, shared with this person (listSharedLibraryFolders) */
    isShared?: boolean;
    /** On a shared project and its folders: what this person may do in its workspaces */
    sharedPermission?: 'view' | 'edit';
    /** On a shared project and its folders: who shared it, by name */
    sharedBy?: string;
}

export interface CreateFolderInput {
    name: string;
    kind: FolderKind;
    parentId: string | null;
    color?: FolderColor;
}

export interface FolderPatch {
    name?: string;
    parentId?: string | null;
    color?: FolderColor;
}

const generateFolderId = (): string => {
    try {
        return crypto.randomUUID();
    } catch {
        return Math.random().toString(36).slice(2, 12) + Date.now().toString(36);
    }
};

// ── IndexedDB mirror ─────────────────────────────────────────────────────────

const localGetAll = async (): Promise<WorkspaceFolder[]> => {
    const db = await openLocalDB();
    return new Promise((resolve, reject) => {
        const request = db.transaction([FOLDERS_STORE], 'readonly').objectStore(FOLDERS_STORE).getAll();
        request.onsuccess = () => resolve((request.result || []) as WorkspaceFolder[]);
        request.onerror = () => reject(request.error);
    });
};

const localPutMany = async (folders: WorkspaceFolder[]): Promise<void> => {
    if (folders.length === 0) return;
    const db = await openLocalDB();
    return new Promise((resolve, reject) => {
        const tx = db.transaction([FOLDERS_STORE], 'readwrite');
        const store = tx.objectStore(FOLDERS_STORE);
        for (const f of folders) store.put(f);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
        tx.onabort = () => reject(tx.error);
    });
};

const localDeleteMany = async (ids: string[]): Promise<void> => {
    if (ids.length === 0) return;
    const db = await openLocalDB();
    return new Promise((resolve, reject) => {
        const tx = db.transaction([FOLDERS_STORE], 'readwrite');
        const store = tx.objectStore(FOLDERS_STORE);
        for (const id of ids) store.delete(id);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
        tx.onabort = () => reject(tx.error);
    });
};

const localGet = async (id: string): Promise<WorkspaceFolder | null> => {
    const db = await openLocalDB();
    return new Promise((resolve, reject) => {
        const request = db.transaction([FOLDERS_STORE], 'readonly').objectStore(FOLDERS_STORE).get(id);
        request.onsuccess = () => resolve((request.result as WorkspaceFolder) || null);
        request.onerror = () => reject(request.error);
    });
};

// ── Firestore ────────────────────────────────────────────────────────────────

const cloudEnabled = (cloudSyncEnabled: boolean): boolean => isFirebaseActive() && cloudSyncEnabled;

const firestoreCollection = () => collection(getFirestore(getFirebaseApp()!), 'folders');

/** Run a cloud write with the offline/timeout guard and a user-facing error. */
const cloudWrite = async (label: string, run: () => Promise<unknown>): Promise<void> => {
    try {
        await withCloudTimeout(run);
    } catch (e) {
        console.error(`[workspaceFolders] ${label} failed:`, e);
        throw new Error(describeCloudError(e));
    }
};

const toFolder = (id: string, data: Record<string, any>): WorkspaceFolder => ({
    id,
    ownerId: data.ownerId,
    name: data.name || 'Untitled',
    kind: data.kind === 'project' ? 'project' : 'folder',
    parentId: data.kind === 'project' ? null : (data.parentId ?? null),
    color: data.color,
    createdAt: data.createdAt || 0,
    updatedAt: data.updatedAt || data.createdAt || 0,
    ...(Array.isArray(data.sharedUserIds) && data.sharedUserIds.length > 0 ? { sharedUserIds: data.sharedUserIds.filter((id: unknown) => typeof id === 'string') } : {}),
});

// ── Public API ───────────────────────────────────────────────────────────────

/**
 * All projects and folders owned by the user. With cloud sync on, Firestore wins
 * and the local mirror is rewritten to match; if Firestore is unreachable the
 * mirror is returned so the library still renders offline.
 */
export const listWorkspaceFolders = async (userId: string, cloudSyncEnabled: boolean = false): Promise<WorkspaceFolder[]> => {
    requireAuth(userId, 'listWorkspaceFolders');
    const readLocal = async () => (await localGetAll()).filter(f => f.ownerId === userId);

    if (!cloudEnabled(cloudSyncEnabled) || isOffline()) return readLocal();

    try {
        const snapshot = await withCloudTimeout(() => getDocs(query(firestoreCollection(), where('ownerId', '==', userId))));
        if (snapshot.metadata?.fromCache) {
            // Not a live server read: never let a cached/empty answer wipe the mirror.
            return readLocal();
        }
        const cloud = snapshot.docs.map(d => toFolder(d.id, d.data()));
        // Rewrite the mirror for this user so deleted folders never come back.
        try {
            const stale = (await readLocal()).filter(f => !cloud.some(c => c.id === f.id)).map(f => f.id);
            await localDeleteMany(stale);
            await localPutMany(cloud);
        } catch (mirrorErr) {
            console.warn('[listWorkspaceFolders] Mirror refresh failed (non-fatal):', mirrorErr);
        }
        return cloud;
    } catch (e) {
        console.warn('[listWorkspaceFolders] Cloud read failed, using local mirror:', e);
        return readLocal();
    }
};

/**
 * Other people's projects shared with this person, with every folder in them, read from the
 * cloud only. Each is marked isShared, with this person's permission and who shared it; their
 * own folders never come back from here. Empty when signed out, offline or on failure.
 */
export const listSharedLibraryFolders = async (userId: string): Promise<WorkspaceFolder[]> => {
    if (!userId || !isFirebaseActive() || isOffline()) return [];
    try {
        const snapshot = await withCloudTimeout(() => getDocs(query(firestoreCollection(), where('sharedUserIds', 'array-contains', userId))));
        const docs = snapshot.docs.filter(d => d.data().ownerId !== userId);
        // A folder says which project it is in; the project says what this person may do
        const projects = new Map(docs.filter(d => d.data().kind === 'project').map(d => [d.id, d.data()]));
        return docs.flatMap(d => {
            const data = d.data();
            const project = data.kind === 'project' ? data : projects.get(data.sharedProjectId);
            if (!project) return [];
            const mine = project.sharedWith?.[userId] ?? data.sharedWith?.[userId];
            return [{
                ...toFolder(d.id, data),
                isShared: true,
                sharedPermission: mine?.permission === 'edit' ? 'edit' as const : 'view' as const,
                sharedBy: mine?.sharedByName || project.ownerName || undefined,
            }];
        });
    } catch (e) {
        console.warn('[listSharedLibraryFolders] Could not read shared projects:', e);
        return [];
    }
};

/** Create a project (parentId null) or a folder (parentId set). Throws if the cloud write fails. */
export const createWorkspaceFolder = async (
    userId: string,
    input: CreateFolderInput,
    cloudSyncEnabled: boolean = false,
): Promise<WorkspaceFolder> => {
    requireAuth(userId, 'createWorkspaceFolder');
    const name = normalizeFolderName(input.name);
    if (!name) throw new Error('Name is required');
    if (input.kind === 'folder' && !input.parentId) throw new Error('Folders must live inside a project');

    const now = Date.now();
    const folder: WorkspaceFolder = {
        id: generateFolderId(),
        ownerId: userId,
        name,
        kind: input.kind,
        parentId: input.kind === 'project' ? null : input.parentId,
        ...(input.color ? { color: input.color } : {}),
        createdAt: now,
        updatedAt: now,
    };
    logger.debug('Storage', `createWorkspaceFolder | ${folder.kind} "${folder.name}" | parent: ${folder.parentId ?? 'root'}`);

    if (cloudEnabled(cloudSyncEnabled)) {
        const { id, ...data } = folder;
        await cloudWrite('create', () => setDoc(doc(firestoreCollection(), id), data));
    }
    await localPutMany([folder]);
    return folder;
};

/** Rename, recolour or re-parent a folder. Throws if the cloud write fails. */
export const updateWorkspaceFolder = async (
    userId: string,
    folderId: string,
    patch: FolderPatch,
    cloudSyncEnabled: boolean = false,
): Promise<void> => {
    requireAuth(userId, 'updateWorkspaceFolder');
    validateParams({ folderId }, 'updateWorkspaceFolder');

    const clean: Record<string, any> = {};
    if (patch.name !== undefined) {
        const name = normalizeFolderName(patch.name);
        if (!name) throw new Error('Name is required');
        clean.name = name;
    }
    if (patch.parentId !== undefined) clean.parentId = patch.parentId;
    if (patch.color !== undefined) clean.color = patch.color;
    if (Object.keys(clean).length === 0) return;
    clean.updatedAt = Date.now();

    if (cloudEnabled(cloudSyncEnabled)) {
        await cloudWrite('update', () => setDoc(doc(firestoreCollection(), folderId), clean, { merge: true }));
    }
    const existing = await localGet(folderId);
    if (existing) await localPutMany([{ ...existing, ...clean }]);
};

/** Delete one or more folders (callers pass the whole subtree). Throws if the cloud write fails. */
export const deleteWorkspaceFolders = async (
    userId: string,
    folderIds: string[],
    cloudSyncEnabled: boolean = false,
): Promise<void> => {
    requireAuth(userId, 'deleteWorkspaceFolders');
    const ids = Array.from(new Set(folderIds.filter(Boolean)));
    if (ids.length === 0) return;

    if (cloudEnabled(cloudSyncEnabled)) {
        const db = getFirestore(getFirebaseApp()!);
        const BATCH = 400; // Firestore allows 500 ops per batch
        for (let i = 0; i < ids.length; i += BATCH) {
            const batch = writeBatch(db);
            for (const id of ids.slice(i, i + BATCH)) batch.delete(doc(db, 'folders', id));
            await cloudWrite('delete', () => batch.commit());
        }
    }
    await localDeleteMany(ids);
};
